import React, { useEffect, useRef, useState } from 'react';

import { DatePicker } from 'antd';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { ChevronDown, ChevronLeft, ChevronRight, ReceiptText, RefreshCw } from 'lucide-react';

import Modal from '@/components/common/Modal';
import { ProjectApi } from '@/services/api/projectApi';
import type { PointsHistoryCategory, PointsHistoryData, PointsSessionUsageItem } from '@/types';

import {
  BILLING_DATE_PRESETS,
  type BillingDatePreset,
  type BillingDateRange,
  createBillingDateRange,
  getBillingDatePresetLabel,
} from '../utils/billingUsageFormat';
import {
  collectPointsHistoryTaskSessions,
  formatPointsHistoryAmount,
  formatPointsHistoryStatusSummary,
  formatPointsHistoryTime,
  getPointsHistoryAmount,
  getPointsHistoryCategory,
  getPointsHistoryCategoryLabel,
  getPointsHistoryTitle,
  summarizePointsHistoryTaskProjects,
} from '../utils/pointsHistoryDisplay';
import { resolvePointsHistoryPageInput } from '../utils/pointsHistoryPagination';
import ProjectNameFilterPopover, { type ProjectFilterOption } from './ProjectNameFilterPopover';
import SessionIdFilterPopover from './SessionIdFilterPopover';

interface PointsHistoryModalProps {
  open: boolean;
  history: PointsHistoryData | null;
  loading: boolean;
  error: string | null;
  userLabel: string;
  activeCategory: PointsHistoryCategory;
  dateRange: BillingDateRange;
  projectFilterKey: string;
  sessionFilterKey: string;
  sessionOptions: PointsSessionUsageItem[];
  sessionOptionsLoading: boolean;
  page: number;
  pageSize: number;
  onClose: () => void;
  onBack: () => void;
  onRetry: () => void;
  onCategoryChange: (category: PointsHistoryCategory) => void;
  onDateRangeChange: (value: BillingDateRange) => void;
  onProjectFilterChange: (projectKey: string) => void;
  onSessionFilterChange: (conversationKey: string) => void;
  onPageChange: (page: number) => void;
}

const filters: Array<{ value: PointsHistoryCategory; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'earn', label: '获得积分' },
  { value: 'refund', label: '返还积分' },
  { value: 'consume', label: '消耗积分' },
];

const categoryClassName: Record<Exclude<PointsHistoryCategory, 'all'>, string> = {
  earn: 'bg-theme-color-10 text-theme-color',
  refund: 'bg-theme-color-10 text-theme-color',
  consume: 'bg-accent-red-10 text-accent-red',
};

const amountClassName = (amount: number): string => {
  return amount >= 0 ? 'text-theme-color' : 'text-accent-red';
};

const historyTableColumns = 'grid-cols-[1.5fr_100px_1fr_1.3fr_60px_120px]';
const datePickerDisplayFormat = 'YYYY/MM/DD';
const dateRangeValueFormat = 'YYYY-MM-DD';

const parseDateRangeValue = (value: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, dateRangeValueFormat, true);
  return parsed.isValid() ? parsed : null;
};

const formatDateRangeValue = (value: Dayjs | null): string => {
  return value ? value.format(dateRangeValueFormat) : '';
};

/** 时间预设文案：积分明细内「全部」显示为「全部日期」，其余沿用通用文案 */
const getPointsHistoryDatePresetLabel = (preset: BillingDatePreset): string => {
  return preset === 'all' ? '全部日期' : getBillingDatePresetLabel(preset);
};

/** 时间预设自定义下拉组件 */
const DatePresetSelect: React.FC<{
  value: BillingDatePreset;
  disabled?: boolean;
  onChange: (preset: BillingDatePreset) => void;
}> = ({ value, disabled = false, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        className="flex h-9 min-w-[90px] items-center justify-between gap-2 rounded-lg border border-dark-border bg-ash px-3 text-sm text-dark-text outline-none transition-colors hover:border-theme-color disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span>{getPointsHistoryDatePresetLabel(value)}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-dark-text-secondary transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[100px] overflow-hidden rounded-xl border border-dark-border bg-dark-card shadow-shadow-2">
          {BILLING_DATE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => {
                onChange(preset);
                setIsOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                preset === value
                  ? 'bg-theme-color/20 text-theme-color'
                  : 'text-dark-text hover:bg-dark-bg'
              }`}
            >
              {getPointsHistoryDatePresetLabel(preset)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PointsHistoryModal: React.FC<PointsHistoryModalProps> = ({
  open,
  history,
  loading,
  error,
  userLabel,
  activeCategory,
  dateRange,
  projectFilterKey,
  sessionFilterKey,
  sessionOptions,
  sessionOptionsLoading,
  page,
  pageSize,
  onClose,
  onBack,
  onRetry,
  onCategoryChange,
  onDateRangeChange,
  onProjectFilterChange,
  onSessionFilterChange,
  onPageChange,
}) => {
  const items = history?.items ?? [];
  const total = history?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [pageInputValue, setPageInputValue] = useState(String(page));
  const [projectOptions, setProjectOptions] = useState<ProjectFilterOption[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);
  const projectOptionsLoadedRef = useRef(false);

  useEffect(() => {
    setPageInputValue(String(page));
  }, [page]);

  // 首次打开时加载项目列表作为表头筛选项，失败时展示空状态，不影响流水加载。
  useEffect(() => {
    if (!open || projectOptionsLoadedRef.current) return;
    projectOptionsLoadedRef.current = true;
    setProjectOptionsLoading(true);
    void ProjectApi.listProjects()
      .then((projects) => {
        setProjectOptions(
          projects.map((project) => ({ value: project.project_key, label: project.title })),
        );
      })
      .catch(() => {
        setProjectOptions([]);
      })
      .finally(() => {
        setProjectOptionsLoading(false);
      });
  }, [open]);

  const isUsageFilterActive = Boolean(projectFilterKey);
  const summaryNetPoints = history?.summary?.net_points;

  const commitPageInput = () => {
    const nextPage = resolvePointsHistoryPageInput(pageInputValue, totalPages, page);
    setPageInputValue(String(nextPage));
    if (nextPage !== page) {
      onPageChange(nextPage);
    }
  };

  return (
    <Modal
      title="积分明细"
      titleCenter
      open={open}
      onClose={onClose}
      onBack={onBack}
      width="min(910px, calc(100vw - 48px))"
      bodyClassName="flex flex-col"
      bodyStyle={{ height: 'min(620px, calc(100vh - 180px))', overflow: 'hidden' }}
    >
      <div
        data-testid="points-history-filter-bar"
        className="-mx-6 -mt-4 flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 px-6 pb-2 pt-4"
      >
        {/* 左：类型 tab */}
        <div className="flex flex-wrap gap-2">
          {filters.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => onCategoryChange(item.value)}
              className={`h-9 rounded-lg border px-4 text-sm transition-colors ${
                activeCategory === item.value
                  ? 'border-theme-color bg-theme-color text-white'
                  : 'border-dark-border bg-dark-card text-dark-text-secondary hover:border-theme-color hover:text-dark-text'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* 右：[项目筛选汇总] + [日期输入（仅 custom）] + [时间预设下拉] */}
        <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 max-[760px]:w-full">
          {activeCategory === 'all' && isUsageFilterActive && summaryNetPoints !== undefined ? (
            <span
              data-testid="points-history-usage-summary"
              aria-live="polite"
              className="mr-2 whitespace-nowrap text-sm text-dark-text-secondary"
            >
              已使用{' '}
              <span className="text-[13px] font-normal tabular-nums text-dark-text">
                {summaryNetPoints}
              </span>
              <span className="ml-0.5">积分</span>
            </span>
          ) : null}
          {dateRange.preset === 'custom' && (
            <>
              <DatePicker
                value={parseDateRangeValue(dateRange.startDate)}
                locale={zhCN}
                format={datePickerDisplayFormat}
                placeholder="开始日期"
                inputReadOnly={false}
                disabled={loading}
                className="points-history-date-picker"
                popupClassName="points-history-date-picker-dropdown"
                onChange={(value) => {
                  const next = {
                    ...dateRange,
                    preset: 'custom' as const,
                    startDate: formatDateRangeValue(value),
                  };
                  if (next.startDate && next.endDate && next.startDate > next.endDate) {
                    next.endDate = next.startDate;
                  }
                  onDateRangeChange(next);
                }}
              />
              <span className="text-xs text-dark-text-secondary">至</span>
              <DatePicker
                value={parseDateRangeValue(dateRange.endDate)}
                locale={zhCN}
                format={datePickerDisplayFormat}
                placeholder="结束日期"
                inputReadOnly={false}
                disabled={loading}
                className="points-history-date-picker"
                popupClassName="points-history-date-picker-dropdown"
                onChange={(value) => {
                  const next = {
                    ...dateRange,
                    preset: 'custom' as const,
                    endDate: formatDateRangeValue(value),
                  };
                  if (next.startDate && next.endDate && next.startDate > next.endDate) {
                    next.startDate = next.endDate;
                  }
                  onDateRangeChange(next);
                }}
              />
            </>
          )}
          <DatePresetSelect
            value={dateRange.preset}
            disabled={loading}
            onChange={(preset) => {
              if (preset === 'custom') {
                onDateRangeChange({ ...dateRange, preset: 'custom' });
              } else {
                onDateRangeChange(createBillingDateRange(preset));
              }
            }}
          />
        </div>
      </div>

      <div data-testid="points-history-records" className="min-h-0 flex-1 flex flex-col pt-5">
        {loading && !history ? (
          <div className="space-y-3 overflow-y-auto">
            <div className="h-10 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            <div className="h-20 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            <div className="h-20 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
            <div className="h-20 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
          </div>
        ) : error ? (
          <div className="flex h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dark-border bg-dark-card">
            <p className="text-sm font-medium text-dark-text">积分明细加载失败</p>
            <p className="max-w-sm text-center text-sm text-dark-text-secondary">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-theme-color px-4 text-sm text-theme-color transition-colors hover:bg-theme-color-10"
            >
              <RefreshCw className="h-4 w-4" />
              重试
            </button>
          </div>
        ) : (
          <div className="relative min-h-0 flex flex-1 flex-col overflow-hidden">
            {loading && history ? (
              <div
                data-testid="points-history-refresh-overlay"
                className="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-dark-bg/60"
              >
                <span className="flex items-center gap-2 rounded-full border border-dark-border bg-dark-card px-4 py-2 text-sm text-dark-text-secondary">
                  <span className="session-status-spinner block h-4 w-4 rounded-full border-2 border-theme-color border-t-transparent animate-spin" />
                  加载中…
                </span>
              </div>
            ) : null}
            <div className="h-0 flex flex-1 flex-col overflow-y-auto rounded-lg border border-dark-border">
              <div
                className={`sticky top-0 z-10 grid shrink-0 ${historyTableColumns} border-b border-dark-border bg-dark-card text-xs font-medium text-dark-text-secondary`}
                style={{ backgroundColor: 'rgb(var(--color-dark-card))' }}
              >
                <span className="whitespace-nowrap px-4 py-2">事项</span>
                <span className="whitespace-nowrap px-4 py-2 text-center">积分变动</span>
                <span className="flex items-center justify-center gap-0.5 whitespace-nowrap px-2 py-2 pl-4">
                  <span>项目名称</span>
                  {activeCategory === 'all' ? (
                    <ProjectNameFilterPopover
                      value={projectFilterKey}
                      options={projectOptions}
                      disabled={loading}
                      optionsLoading={projectOptionsLoading}
                      onChange={onProjectFilterChange}
                    />
                  ) : null}
                </span>
                <span className="flex items-center justify-center gap-1 whitespace-nowrap px-2 py-2">
                  <span>会话</span>
                  {activeCategory === 'all' ? (
                    <SessionIdFilterPopover
                      value={sessionFilterKey}
                      options={sessionOptions}
                      disabled={loading || !projectFilterKey}
                      optionsLoading={sessionOptionsLoading}
                      onChange={onSessionFilterChange}
                    />
                  ) : null}
                </span>
                <span className="whitespace-nowrap px-4 py-2 text-center">任务数</span>
                <span className="whitespace-nowrap px-4 py-2 text-center">状态</span>
              </div>
              {items.length > 0 ? (
                <div className="shrink-0">
                  {items.map((item) => {
                    const amount = getPointsHistoryAmount(item);
                    const category = getPointsHistoryCategory(item) as Exclude<
                      PointsHistoryCategory,
                      'all'
                    >;
                    const relatedTasks = item.related_tasks ?? [];
                    return (
                      <div
                        key={`${item.batch_id}-${item.gmt_create}`}
                        className={`grid ${historyTableColumns} items-center border-b border-dark-border last:border-b-0`}
                      >
                        <div className="min-w-0 px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="min-w-0 truncate text-sm font-medium text-dark-text">
                              {getPointsHistoryTitle(item)}
                            </p>
                            <span
                              className={`rounded-full px-2 py-0.5 text-[11px] ${categoryClassName[category]}`}
                            >
                              {getPointsHistoryCategoryLabel(category)}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-dark-text-secondary">
                            {formatPointsHistoryTime(item.gmt_create)}
                          </p>
                        </div>
                        <span
                          className={`px-4 py-3 text-center text-sm font-semibold tabular-nums ${amountClassName(amount)}`}
                        >
                          {formatPointsHistoryAmount(amount)}
                        </span>
                        <span className="break-words px-4 py-3 text-center text-xs font-medium text-dark-text">
                          {summarizePointsHistoryTaskProjects(relatedTasks)}
                        </span>
                        <span
                          data-testid="points-history-session-cell"
                          className="min-w-0 overflow-hidden break-words px-4 py-3 text-center text-xs font-medium text-dark-text"
                        >
                          {(() => {
                            const sessions = collectPointsHistoryTaskSessions(
                              relatedTasks,
                              userLabel,
                            );
                            if (sessions.length === 0) return '--';
                            return (
                              <span className="flex min-w-0 flex-col items-center gap-1 overflow-hidden">
                                {sessions.map((session) => (
                                  <span
                                    key={session.sessionId}
                                    className="block w-full min-w-0 leading-tight"
                                  >
                                    <span className="block">{session.sessionId}</span>
                                    {session.title ? (
                                      <span
                                        className="mt-0.5 block truncate text-[11px] font-normal text-dark-text-secondary"
                                        title={session.title}
                                      >
                                        {session.title}
                                      </span>
                                    ) : null}
                                  </span>
                                ))}
                              </span>
                            );
                          })()}
                        </span>
                        <span className="px-4 py-3 text-center text-xs text-dark-text-secondary">
                          {relatedTasks.length > 0 ? `${relatedTasks.length}` : '--'}
                        </span>
                        <span className="px-4 py-3 text-center text-xs text-dark-text-secondary">
                          {(() => {
                            const statusParts = formatPointsHistoryStatusSummary(relatedTasks);
                            if (statusParts.length === 0) return '--';
                            if (statusParts.length === 1) return statusParts[0];
                            return (
                              <span className="flex flex-col items-center leading-tight">
                                {statusParts.map((part) => (
                                  <span key={part}>{part}</span>
                                ))}
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div
                  data-testid="points-history-empty-state"
                  className="flex min-h-0 flex-1 flex-col items-center justify-center bg-dark-card text-center"
                >
                  <ReceiptText className="mb-3 h-7 w-7 text-dark-text-secondary" />
                  <p className="text-sm font-medium text-dark-text">暂无积分明细</p>
                </div>
              )}
            </div>

            <div
              data-testid="points-history-pagination"
              className="shrink-0 flex flex-wrap items-center justify-center gap-3 pt-4 text-xs text-dark-text-secondary"
            >
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={loading || page <= 1}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-dark-border px-3 transition-colors hover:border-theme-color hover:text-dark-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                上一页
              </button>
              <span className="inline-flex items-center gap-1 tabular-nums">
                第
                <input
                  data-testid="points-history-page-input"
                  aria-label="跳转页码"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pageInputValue}
                  disabled={loading}
                  onChange={(event) => setPageInputValue(event.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={commitPageInput}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.currentTarget.blur();
                    }
                  }}
                  className="h-8 w-14 rounded-lg border border-dark-border bg-dark-bg px-2 text-center text-xs text-dark-text outline-none transition-colors focus:border-theme-color disabled:cursor-not-allowed disabled:opacity-50"
                />
                / {totalPages} 页，共 {total} 条
              </span>
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={loading || page >= totalPages}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-dark-border px-3 transition-colors hover:border-theme-color hover:text-dark-text disabled:cursor-not-allowed disabled:opacity-50"
              >
                下一页
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PointsHistoryModal;
